/**
 * Construye un SOP GSPS (PR) a partir de mediciones de sesión.
 */
import { GSPS_SOP_CLASS } from './gspsParse'
import { measurementsToGraphics } from './gspsMap'
import {
  concat,
  elCS,
  elDA,
  elFL,
  elIS,
  elLO,
  elPN,
  elSH,
  elSQ,
  elST,
  elTM,
  elUI,
  elUS,
  elUSMulti,
  newUid,
  nowDicomDate,
  nowDicomTime,
  wrapPart10,
} from './dicomPart10'

function dsStr(image, tag) {
  if (!image || !image.data || typeof image.data.string !== 'function') return ''
  try {
    return image.data.string(tag) || ''
  } catch (e) {
    return ''
  }
}

function graphicItem(g) {
  const n = Math.floor((g.graphicData || []).length / 2)
  return concat([
    elCS('x00700023', g.graphicType || 'POLYLINE'),
    elUS('x00700021', n),
    elFL('x00700022', g.graphicData),
    elCS('x00700024', 'N'),
  ])
}

function textItem(g) {
  if (!g || !g.label) return null
  const x = (g.graphicData && g.graphicData[0]) || 1
  const y = (g.graphicData && g.graphicData[1]) || 1
  return concat([
    elCS('x00700004', 'PIXEL'),
    elST('x00700006', g.label),
    elFL('x00700014', [x, y]),
    elCS('x00700015', 'Y'),
  ])
}

/**
 * @param {{
 *   measurements: Array,
 *   image: object,
 *   fileData?: object,
 * }} ctx
 * @returns {{ bytes: Uint8Array, sopInstanceUid: string, sopClassUid: string, graphics: object[], referencedSop: string }}
 */
export function buildGspsFile(ctx) {
  const image = (ctx && ctx.image) || {}
  const fileData = (ctx && ctx.fileData) || {}
  const graphics = measurementsToGraphics(ctx && ctx.measurements)
  const date = nowDicomDate()
  const time = nowDicomTime()
  const sopInstanceUid = newUid()
  const seriesUid = newUid()
  const studyUid =
    dsStr(image, 'x0020000d') ||
    (fileData.study && fileData.study.studyId) ||
    newUid()
  const refSop =
    dsStr(image, 'x00080018') || fileData.sopInstanceUID || ''
  const refClass = dsStr(image, 'x00080016') || '1.2.840.10008.5.1.4.1.1.2'
  const cols = image.columns || fileData.columns || 512
  const rows = image.rows || fileData.rows || 512
  const patientName = dsStr(image, 'x00100010') || 'ANONYMOUS'
  const patientId = dsStr(image, 'x00100020') || '0'
  const refSeriesUid = dsStr(image, 'x0020000e') || seriesUid

  const refImageItem = concat([
    elUI('x00081150', refClass),
    elUI('x00081155', refSop || sopInstanceUid),
  ])
  const refSeriesItem = concat([
    elUI('x0020000e', refSeriesUid),
    elSQ('x00081140', [refImageItem]),
  ])

  const displayedItem = concat([
    elSQ('x00081140', [refImageItem]),
    elUSMulti('x00700052', [1, 1]),
    elUSMulti('x00700053', [cols, rows]),
    elCS('x00700100', 'SCALE TO FIT'),
  ])

  const goItems = graphics.map(graphicItem)
  const tx = graphics.map(textItem).filter(Boolean)
  const annotationParts = [
    elCS('x00700002', 'UDV'),
    elSQ('x00081140', [refImageItem]),
    elSQ('x00700009', goItems),
  ]
  if (tx.length) annotationParts.push(elSQ('x00700008', tx))
  const annotationItem = concat(annotationParts)

  const layerItem = concat([
    elCS('x00700002', 'UDV'),
    elIS('x00700062', '1'),
    elLO('x00700068', 'UDV annotations'),
  ])

  const dataset = concat([
    elUI('x00080016', GSPS_SOP_CLASS),
    elUI('x00080018', sopInstanceUid),
    elDA('x00080020', dsStr(image, 'x00080020') || date),
    elDA('x00080023', date),
    elTM('x00080030', dsStr(image, 'x00080030') || time),
    elTM('x00080033', time),
    elCS('x00080060', 'PR'),
    elLO('x0008103e', 'UDV Presentation State'),
    elSQ('x00081115', [refSeriesItem]),
    elPN('x00100010', patientName),
    elLO('x00100020', patientId),
    elUI('x0020000d', studyUid),
    elUI('x0020000e', seriesUid),
    elSH('x00200010', dsStr(image, 'x00200010') || '1'),
    elIS('x00200011', '99'),
    elSQ('x00700001', [annotationItem]),
    elSQ('x0070005a', [displayedItem]),
    elSQ('x00700060', [layerItem]),
    elDA('x00700082', date),
    elTM('x00700083', time),
    elPN('x00700084', 'tjs-udv'),
  ])

  const bytes = wrapPart10(dataset, {
    sopClassUid: GSPS_SOP_CLASS,
    sopInstanceUid,
  })
  return {
    bytes,
    sopInstanceUid,
    sopClassUid: GSPS_SOP_CLASS,
    graphics,
    referencedSop: refSop,
  }
}
